import FormControl from "@/components/FormControl/FormControl";
import type { ComponentProps } from "react";
import type { FormControlType } from "@/components/FormControl/FormControl";
import type { Control, FieldValues, UseFormRegister, UseFormStateReturn } from "react-hook-form";

export interface FormFieldConfig {
  controlType: FormControlType;
  label: string;
  // "caption": small, uppercase, muted-gray, letter-spaced (Export/Add
  // Member modals, stat cards). "default" (or omitted): Login's sentence-
  // case look. See `components/FormControl/withLabelAndError.tsx`.
  labelVariant?: "default" | "caption";
  placeholder?: string;
  defaultValue?: unknown;
  validations?: Record<string, unknown>;
  isRequired?: boolean;
  autoComplete?: string;
  isControlled?: boolean;
  props?: Record<string, unknown>;
  isDisabled?: boolean;
  hide?: boolean;
}

export type FormConfig = Record<string, FormFieldConfig>;

export default function formRenderer<TFieldValues extends FieldValues>(
  formConfig: FormConfig,
  formState: UseFormStateReturn<TFieldValues>,
  register: UseFormRegister<TFieldValues>,
  control?: Control<TFieldValues>,
) {
  return Object.entries(formConfig || {}).map(([name, obj]) => {
    if (obj?.hide) return null;

    const fieldError = formState?.errors?.[name as keyof typeof formState.errors];
    const isFieldDisabled = obj.isDisabled || formState?.isSubmitting;
    const disabledProp = obj.controlType === "select" ? { isDisabled: isFieldDisabled } : { disabled: isFieldDisabled };

    // `controlType` is data-driven here (chosen by `formConfig`, not a JSX
    // literal), so it can't line up with FormControl's discriminated union
    // at compile time the way `<FormControl controlType="input" .../>`
    // does — that's inherent to a generic config-driven renderer, not a gap
    // to type around. Each individual FormControl variant still gets full
    // type-checking at its own call sites.
    const formControlProps = {
      name,
      controlType: obj.controlType,
      label: obj.label,
      labelVariant: obj.labelVariant,
      placeholder: obj.placeholder || `Enter ${obj.label}`,
      validations: obj.validations,
      errorMessage: (fieldError as { message?: string } | undefined)?.message,
      isRequired: obj.isRequired,
      autoComplete: obj.autoComplete || "new-password",
      ...disabledProp,
      ...(obj.isControlled && control ? { control } : { register }),
      ...(obj.props || {}),
    } as ComponentProps<typeof FormControl>;

    return <FormControl key={name} {...formControlProps} />;
  });
}
